import { useEffect, useState, useCallback, useMemo } from 'react';
import AdminSearch, { matchesQuery } from '../../components/admin/AdminSearch';
import { Link } from 'react-router-dom';
import { Plus, AlertTriangle, Trash2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { deleteProduct } from '../../lib/deleteProduct';
import ConfirmDialog from '../../components/ConfirmDialog';

function money(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

const STALE_DAYS = { volatile: 3, moderate: 21, stable: Infinity };

function isStale(product) {
  const maxDays = STALE_DAYS[product.volatility] ?? Infinity;
  if (maxDays === Infinity) return false;
  if (!product.price_checked_at) return true;
  const ageMs = Date.now() - new Date(product.price_checked_at).getTime();
  return ageMs > maxDays * 24 * 60 * 60 * 1000;
}

const VOLATILITY_COLOR = { stable: '#22c55e', moderate: '#f59e0b', volatile: '#ef4444' };

const OWNER_LABEL = { trainer_center: 'Trainer Center', olin: 'Olin' };

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | stale | draft | active | archived
  const [query, setQuery] = useState('');
  const [confirmProduct, setConfirmProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  // Owner-level storefront kill switch. One row per owner, not a change to any
  // product — so unhiding restores exactly what was live before, drafts stay
  // drafts, and nothing has to be remembered or put back by hand.
  const [visibility, setVisibility] = useState([]);
  const [visBusy, setVisBusy] = useState('');

  const askDelete = (e, product) => {
    // The rows/cards are links to the edit page — don't navigate on delete.
    e.preventDefault();
    e.stopPropagation();
    setDeleteError('');
    setConfirmProduct(product);
  };

  const confirmDelete = async () => {
    if (!confirmProduct) return;
    setDeleting(true);
    const { error } = await deleteProduct(confirmProduct.id);
    setDeleting(false);
    if (error) { setDeleteError(error.message); return; }
    setProducts((prev) => prev.filter((p) => p.id !== confirmProduct.id));
    setConfirmProduct(null);
  };

  const load = useCallback(async () => {
    const [{ data: prods }, { data: cats }, { data: vis }] = await Promise.all([
      supabase.from('products').select('*').order('updated_at', { ascending: false }),
      supabase.from('categories').select('id, name'),
      supabase.from('sv_owner_visibility').select('owner, hidden').order('owner'),
    ]);
    setProducts(prods || []);
    setCategories(Object.fromEntries((cats || []).map((c) => [c.id, c.name])));
    setVisibility(vis || []);
    setLoading(false);
  }, []);

  const toggleOwner = async (owner, hide) => {
    setVisBusy(owner);
    const { data: me } = await supabase.auth.getUser();
    await supabase.from('sv_owner_visibility').update({
      hidden: hide,
      hidden_at: hide ? new Date().toISOString() : null,
      hidden_by: hide ? me?.user?.id || null : null,
      updated_at: new Date().toISOString(),
    }).eq('owner', owner);
    await load();
    setVisBusy('');
  };

  useEffect(() => { load(); }, [load]);

  const byStatus = useMemo(() => {
    if (filter === 'all') return products;
    if (filter === 'stale') return products.filter(isStale);
    return products.filter((p) => p.status === filter);
  }, [products, filter]);

  // Everything a person might plausibly type to find a card: name, set, card
  // number, rarity, condition/grade, language, category, slug, and the price
  // both as dollars and as raw cents.
  const filtered = useMemo(() => {
    if (!query.trim()) return byStatus;
    return byStatus.filter((p) => matchesQuery(query, [
      p.name, p.set_name, p.number, p.rarity, p.condition, p.grade,
      p.language, p.slug, categories[p.category_id],
      p.price_cents != null ? (p.price_cents / 100).toFixed(2) : '',
    ]));
  }, [byStatus, query, categories]);

  const staleCount = useMemo(() => products.filter(isStale).length, [products]);

  if (loading) return <div>Loading…</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 900 }}>Products</h1>
        <Link to="/admin/products/add" className="sv-btn"><Plus size={16} /> Add products</Link>
      </div>

      {/* Storefront visibility per owner. Takes effect immediately and does not
          alter a single product — a hidden owner's items keep their own
          draft/active status and come back exactly as they were. */}
      <div
        className="sv-card"
        style={{ padding: 14, marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-muted)' }}>
          Storefront
        </span>
        {visibility.map((v) => {
          const count = products.filter((p) => p.owner === v.owner && p.status === 'active').length;
          return (
            <button
              key={v.owner}
              type="button"
              onClick={() => toggleOwner(v.owner, !v.hidden)}
              disabled={visBusy === v.owner}
              className={v.hidden ? 'sv-btn' : 'sv-btn-outline'}
              style={{ padding: '7px 12px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              title={v.hidden
                ? `Put ${OWNER_LABEL[v.owner] || v.owner}'s products back on the storefront`
                : `Pull all of ${OWNER_LABEL[v.owner] || v.owner}'s products off the storefront now`}
            >
              {v.hidden ? <EyeOff size={14} /> : <Eye size={14} />}
              {v.hidden
                ? `Unhide ${OWNER_LABEL[v.owner] || v.owner}`
                : `Hide ${OWNER_LABEL[v.owner] || v.owner}`}
              <span style={{ opacity: 0.6 }}>({count})</span>
            </button>
          );
        })}
        {visibility.some((v) => v.hidden) && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', color: 'var(--danger)', fontWeight: 700 }}>
            <AlertTriangle size={14} />
            {visibility.filter((v) => v.hidden).map((v) => OWNER_LABEL[v.owner] || v.owner).join(' and ')}
            {visibility.filter((v) => v.hidden).length === 1 ? ' is' : ' are'} hidden from customers
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['all', 'stale', 'draft', 'active', 'archived'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={filter === f ? 'sv-btn' : 'sv-btn-outline'}
            style={{ padding: '7px 14px', fontSize: '0.82rem' }}
          >
            {f === 'all' && `All (${products.length})`}
            {f === 'stale' && (
              <>
                <AlertTriangle size={14} /> Needs price check ({staleCount})
              </>
            )}
            {f !== 'all' && f !== 'stale' && f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      <AdminSearch
        value={query}
        onChange={setQuery}
        placeholder="Search name, set, number, rarity, condition…"
        resultCount={filtered.length}
        totalCount={byStatus.length}
      />

      {/* phone: tappable cards (iOS-app admin shell); desktop keeps the table */}
      <div className="sv-admin-cardlist">
        {filtered.map((p) => {
          const profit = p.price_cents - p.cost_cents;
          const soldOut = p.status === 'active' && p.quantity_available <= 0;
          return (
            <Link
              key={p.id}
              to={`/admin/products/${p.id}`}
              className="sv-card"
              style={{ padding: '14px 16px', display: 'block' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
                <span style={{ fontWeight: 800, fontSize: '0.95rem' }}>
                  {p.name}
                  {isStale(p) && <AlertTriangle size={14} color="#f59e0b" style={{ marginLeft: 6, verticalAlign: 'middle' }} />}
                </span>
                <span style={{ fontWeight: 800 }}>{money(p.price_cents)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.78rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span>{categories[p.category_id] || 'Uncategorized'}</span>
                <span>Stock {p.quantity_available}</span>
                <span style={{ color: profit >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>{money(profit)} profit</span>
                <span style={{ color: VOLATILITY_COLOR[p.volatility], fontWeight: 700, textTransform: 'uppercase', fontSize: '0.68rem' }}>{p.volatility}</span>
                {soldOut ? (
                  <span style={{ color: 'var(--danger)', fontWeight: 700 }}>SOLD OUT</span>
                ) : (
                  <span style={{ fontWeight: 700 }}>{p.status}</span>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  type="button"
                  onClick={(e) => askDelete(e, p)}
                  className="sv-btn-outline"
                  style={{ padding: '7px 12px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </Link>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No products.</div>
        )}
      </div>

      <div className="sv-admin-tablewrap" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)' }}>
              <th style={{ padding: '8px 10px' }}>Name</th>
              <th style={{ padding: '8px 10px' }}>Category</th>
              <th style={{ padding: '8px 10px' }}>Language</th>
              <th style={{ padding: '8px 10px' }}>Cost</th>
              <th style={{ padding: '8px 10px' }}>Price</th>
              <th style={{ padding: '8px 10px' }}>Profit</th>
              <th style={{ padding: '8px 10px' }}>Stock</th>
              <th style={{ padding: '8px 10px' }}>Volatility</th>
              <th style={{ padding: '8px 10px' }}>Status</th>
              <th style={{ padding: '8px 10px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const profit = p.price_cents - p.cost_cents;
              const margin = p.cost_cents > 0 ? (profit / p.cost_cents) * 100 : null;
              const soldOut = p.status === 'active' && p.quantity_available <= 0;
              return (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px' }}>
                    <Link to={`/admin/products/${p.id}`} style={{ fontWeight: 700 }}>{p.name}</Link>
                    {isStale(p) && <AlertTriangle size={13} color="#f59e0b" style={{ marginLeft: 6, verticalAlign: 'middle' }} />}
                  </td>
                  <td style={{ padding: '10px', color: 'var(--text-muted)' }}>{categories[p.category_id] || '—'}</td>
                  <td style={{ padding: '10px', color: 'var(--text-muted)' }}>{p.language || 'English'}</td>
                  <td style={{ padding: '10px' }}>{money(p.cost_cents)}</td>
                  <td style={{ padding: '10px' }}>{money(p.price_cents)}</td>
                  <td style={{ padding: '10px', color: profit >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                    {money(profit)} {margin !== null && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({margin.toFixed(0)}%)</span>}
                  </td>
                  <td style={{ padding: '10px' }}>{p.quantity_available}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ color: VOLATILITY_COLOR[p.volatility], fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase' }}>
                      {p.volatility}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>
                    {soldOut ? <span style={{ color: 'var(--danger)', fontWeight: 700 }}>Sold out</span> : p.status}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={(e) => askDelete(e, p)}
                      className="sv-btn-outline"
                      style={{ padding: '6px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                      aria-label={`Delete ${p.name}`}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No products.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {confirmProduct && (
        <ConfirmDialog
          title="Delete this product?"
          message={
            deleteError
              ? `Couldn't delete: ${deleteError}`
              : `"${confirmProduct.name}" and its photos will be permanently removed. Past orders that included it are kept. This can't be undone.`
          }
          confirmLabel="Delete product"
          busy={deleting}
          onConfirm={confirmDelete}
          onCancel={() => { if (!deleting) { setConfirmProduct(null); setDeleteError(''); } }}
        />
      )}
    </div>
  );
}
